import { sdk } from '../sdk';

export interface Expense {
  _id: string;
  expenseId: number;
  name: string;
  amount: number;
  expenseTypeId: {
    _id: string;
    name: string;
  };
  expenseTypeName: string;
  accountId: string | {
    _id: string;
    name: string;
    accountNumber: string;
  };
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  userName: string;
  userRole: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseData {
  name: string;
  amount: number;
  expenseTypeId: string;
  accountId: string;
}

export interface UpdateExpenseData {
  name?: string;
  amount?: number;
  expenseTypeId?: string;
  accountId?: string;
}

export interface ExpenseResponse {
  success: boolean;
  data: Expense;
}

export interface ExpensesResponse {
  data: Expense[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ExpenseStats {
  totalExpenses: number;
  totalAmount: number;
  averageAmount: number;
}

export interface ExpenseSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  expenseTypeId?: string;
  startDate?: string;
  endDate?: string;
}

export const ExpensesAPI = {
  // Get all expenses with pagination and filters
  async getAllExpenses(params: ExpenseSearchParams = {}): Promise<ExpensesResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    // Default limit to 50 if not specified
    queryParams.append('limit', (params.limit || 50).toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.expenseTypeId) queryParams.append('expenseTypeId', params.expenseTypeId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await sdk.client.get(`/api/v1/expenses?${queryParams.toString()}`);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  },

  // Get expense statistics
  async getExpenseStats(params: { startDate?: string; endDate?: string; accountId?: string } = {}): Promise<ExpenseStats> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.accountId) queryParams.append('accountId', params.accountId);

    const response = await sdk.client.get(`/api/v1/expenses/stats?${queryParams.toString()}`);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  },

  // Get expense by ID
  async getExpenseById(id: string): Promise<Expense> {
    const response = await sdk.client.get(`/api/v1/expenses/${id}`);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  },

  // Create new expense
  async createExpense(data: CreateExpenseData): Promise<ExpenseResponse> {
    const response = await sdk.client.post('/api/v1/expenses', data);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  },

  // Update expense
  async updateExpense(id: string, data: UpdateExpenseData): Promise<ExpenseResponse> {
    const response = await sdk.client.put(`/api/v1/expenses/${id}`, data);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  },

  // Delete expense
  async deleteExpense(id: string): Promise<{ success: boolean; message: string }> {
    const response = await sdk.client.delete(`/api/v1/expenses/${id}`);
    // Handle both wrapped and unwrapped responses
    return response.data || response;
  }
};
